import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppInsightsService } from '../../exception-handling/app-insights.service ';

// Add Google type definitions
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: any) => void;
          renderButton: (container: HTMLElement, options: any) => void;
          prompt: () => void;
        }
      }
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private googleApiLoaded = false;
  private googleApiLoading = false;
  
  // Observable to track the loading state of the Google API
  private googleApiLoadedSubject = new BehaviorSubject<boolean>(false);
  googleApiLoaded$ = this.googleApiLoadedSubject.asObservable();

  private logService = inject(AppInsightsService);

  constructor() {}

  loadGoogleScript(): Promise<void> {
    // Return immediately if already loaded
    if (this.googleApiLoaded) {
      return Promise.resolve();
    }

    // Return the existing promise if already loading
    if (this.googleApiLoading) {
      return new Promise<void>((resolve) => {
        const subscription = this.googleApiLoaded$.subscribe(loaded => {
          if (loaded) {
            subscription.unsubscribe();
            resolve();
          }
        });
      });
    }

    return new Promise<void>((resolve) => {
      // Check if script already exists in document
      if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
        this.googleApiLoaded = true;
        this.googleApiLoadedSubject.next(true);
        resolve();
        return;
      }

      // Create and append script
      this.googleApiLoading = true;
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.googleApiLoaded = true;
        this.googleApiLoading = false;
        this.googleApiLoadedSubject.next(true);
        resolve();
      };
      script.onerror = () => {
        this.logService.logError(new Error('Failed to load Google API script'), 'GoogleAuthService');
        this.googleApiLoading = false;
        // Resolve anyway to not block the app
        resolve();
      };
      document.body.appendChild(script);
    });
  }

  // Method to initialize Google Sign-In once script is loaded
  initializeGoogleSignIn(clientId: string, callback: (response: any) => void): void {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: callback,
        auto_select: false,
        itp_support: true
      });
    } else {
      this.logService.logWarning('Google API not loaded yet. Cannot initialize Google Sign-In.');
    }
  }

  // Method to render the button in a specific container
  renderGoogleButton(element: HTMLElement, options: any = {}): void {
    if (!element) {
      this.logService.logWarning('Element not provided for Google Sign-In button rendering');
      return;
    }
    
    if (window.google && window.google.accounts && window.google.accounts.id) {
      const defaultOptions = { 
        theme: 'outline', 
        size: 'large', 
        width: 250 
      };
      
      window.google.accounts.id.renderButton(
        element,
        { ...defaultOptions, ...options }
      );
    } else {
      console.warn('Google API not loaded yet. Cannot render Google Sign-In button.');
    }
  }
}